import { Play } from "lucide-react";
import { useState } from "react";
import { youTubeEmbedUrl, youTubeThumbnailUrl } from "@jipraks/shared";
import { cn } from "@client/lib/cn";
import { catalogCopy } from "@client/locales/id/catalog";

const copy = catalogCopy.detail;
const FRAME =
  "relative block aspect-video w-full overflow-hidden rounded-2xl border border-border-default bg-background-elevated shadow-card";

interface ProductTeaserProps {
  videoId: string;
  title: string;
  /** The product cover; falls back to the YouTube thumbnail. */
  posterUrl: string | null;
}

/**
 * Click-to-play: the YouTube player and its scripts load only once the visitor presses play,
 * so the detail page stays as light as it is without a teaser.
 */
export function ProductTeaser({ videoId, title, posterUrl }: ProductTeaserProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  if (isPlaying) {
    return (
      <div className={FRAME}>
        <iframe
          src={youTubeEmbedUrl(videoId)}
          title={copy.teaserFrameTitle(title)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="absolute inset-0 size-full"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsPlaying(true)}
      aria-label={copy.playTeaser(title)}
      className={cn(FRAME, "group cursor-pointer text-left")}
    >
      <img
        src={posterUrl ?? youTubeThumbnailUrl(videoId)}
        alt=""
        width={1280}
        height={720}
        className="size-full object-cover transition-transform duration-300 ease-expo-out group-hover:scale-[1.02]"
      />
      <span aria-hidden="true" className="absolute inset-0 bg-linear-to-t from-background-deep/80 via-background-deep/20 to-transparent" />
      <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
        <span className="flex size-16 items-center justify-center rounded-full bg-accent text-foreground shadow-accent transition-all duration-200 ease-expo-out group-hover:scale-[1.02] group-hover:bg-accent-bright group-hover:shadow-accent-hover md:size-20">
          <Play className="size-7 translate-x-0.5 fill-current md:size-8" />
        </span>
      </span>
      <span aria-hidden="true" className="absolute bottom-4 left-4 font-mono text-xs tracking-widest text-foreground uppercase md:bottom-5 md:left-5">
        {copy.teaserLabel}
      </span>
    </button>
  );
}
